// hooks/useUITheme.ts
//
// Returns the current user's UI theme preference. Reads from
// /users/{uid}/uiTheme via AuthContext; defaults to 'classic' so any
// unset user keeps the original look untouched.
//
// Values:
//   'classic' — the shipping UI.
//   'preview' — the dashboard-redesign preview (rolled out role by
//               role; falls back to classic for roles not built yet).
// A stored 'minimal' (the retired first redesign attempt) is treated
// as 'classic'.
//
// Why a hook and not a CSS variable / context: shell components
// (NavMenu, PageContainer, dashboards) need to branch at the
// render-tree level — different children, different layout,
// different motion behaviour. A boolean check in React is
// cheaper and clearer than a runtime style switch.
//
// Persistence helper setUITheme() lives here too so the Settings
// toggle component doesn't have to know the Firebase path.

import { useCallback } from 'react';
import { ref, update } from 'firebase/database';
import { db } from '../config/firebase';
import { useAuth } from '../context/AuthContext';

export type UITheme = 'classic' | 'preview';

export interface UIThemeValue {
  /** Current theme. Always defined — falls back to 'classic'
   *  when the user has nothing (or the retired 'minimal') on file. */
  theme: UITheme;
  /** Persist a new theme on /users/{uid}/uiTheme. Optimistic
   *  update isn't needed — AuthContext's /users/{uid} listener
   *  picks up the new value on the next snapshot. */
  setTheme: (next: UITheme) => Promise<void>;
  /** Convenience for components that just want a boolean check. */
  isPreview: boolean;
  /** Retired 'minimal' experiment — always false now. Kept so the
   *  remaining legacy consumers compile until they're deleted. */
  isMinimal: boolean;
}

export function useUITheme(): UIThemeValue {
  const { user } = useAuth();
  const theme: UITheme = user?.uiTheme === 'preview' ? 'preview' : 'classic';

  const setTheme = useCallback(
    async (next: UITheme) => {
      if (!user?.uid) return;
      await update(ref(db, `users/${user.uid}`), { uiTheme: next });
    },
    [user?.uid],
  );

  return { theme, setTheme, isPreview: theme === 'preview', isMinimal: false };
}
